import React, { useMemo, useState } from 'react';
import { Tag, Check, Plus, ChevronRight, Library } from 'lucide-react';
import { BaseComponentImpl } from '../../../../../types/implementations';
import { Building, DoorCategory } from '../../../../../types/protocols';
import { useBuildComponentTypes } from '../../../../../contexts/BuildComponentTypesContext';
import { useMaterials } from '../../../../../contexts/MaterialsContext';
import { buildComponentTypeDisplayName } from '../../../../../Helpers/windowTypeName';
import { translate } from '../../../../../types/EnumTranslations';
import { componentFieldsApi } from '../../../../../api/componentFieldsApi';
import { BaseType, CostUnit } from '../../../../../types/Enums';
import { defaultCostUnitForBaseType } from '../../../../../Helpers/baseTypeUtils';
import { LibraryPickerModal } from '../../../../../Components/LibraryPickerModal';
import { lifetimeLibrary } from '../../../../../library/lifetimeLibrary';
import { COST_UNIT_OPTIONS } from '../../../../../library/LibraryConfig';
import { LibraryLifetimeTemplate } from '../../../../../types/LibraryLifetimeTemplate';
import NumberInput from '../../../../../Components/NumberInput';
import {
    ComponentCategory,
    getCategoriesForBaseType,
    getCategoryField,
    getComponentCategory,
    getTypesForCategory
} from '../../../../../Helpers/componentTypeCategories';

// Brand color from index.css
const BRAND_DARK_BLUE = '#001D60';

/** What the user picked in the modal. Category and type are saved together. */
export interface SpecificTypeSelection {
    /** The chosen type key, or null when saving a category without a type. */
    typeKey: string | null;
    /** Set for Utility/Outdoor components; null for base types without categories. */
    category: ComponentCategory | null;
}

interface SpecificTypeModalProps {
    component: BaseComponentImpl;
    building: Building | null;
    onSave: (selection: SpecificTypeSelection) => Promise<void>;
    onClose: () => void;
    /** Open on the category step (Utility/Outdoor only). Defaults to the type step. */
    initialView?: 'category' | 'type';
}

/**
 * Modal for setting a component's specificType — and, for Utility/Outdoor
 * components, its category in the same save.
 *
 * For Utility/Outdoor the modal is a two-level picker: the category is the
 * navigation level and the type is the actual selection, so an incompatible
 * category/type combination cannot be produced. Picking a type saves both
 * atomically via the caller's onSave.
 */
export const SpecificTypeModal: React.FC<SpecificTypeModalProps> = ({
    component,
    building,
    onSave,
    onClose,
    initialView
}) => {
    const { types: buildComponentTypes, refresh } = useBuildComponentTypes();
    const { materials } = useMaterials();
    const [saving, setSaving] = useState<boolean>(false);
    const [savingType, setSavingType] = useState<string | null>(null);
    const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
    const [newTypeName, setNewTypeName] = useState<string>('');
    // NaN = not filled in yet; a cost of 0 is a valid answer (no cost)
    const [newTypeLifetimeYears, setNewTypeLifetimeYears] = useState<number>(NaN);
    const [newTypeReplacementCost, setNewTypeReplacementCost] = useState<number>(NaN);
    const [newTypeCostUnit, setNewTypeCostUnit] = useState<CostUnit>(() => defaultCostUnitForBaseType(component.type));
    const [showLibraryPicker, setShowLibraryPicker] = useState<boolean>(false);
    const [creating, setCreating] = useState<boolean>(false);
    // Set once the type is written, so a retry after a failed selection
    // selects it instead of creating a duplicate
    const [createdTypeKey, setCreatedTypeKey] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Utility/Outdoor components carry a per-component category; the type list
    // is navigated per category and both are saved together.
    const hasCategories = getCategoryField(component.type) !== null;
    const currentCategory = getComponentCategory(component);

    const [selectedCategory, setSelectedCategory] = useState<ComponentCategory | null>(currentCategory);
    const [view, setView] = useState<'category' | 'type'>(
        hasCategories && (initialView === 'category' || !currentCategory) ? 'category' : 'type'
    );

    const categories = useMemo(() => getCategoriesForBaseType(component.type), [component.type]);

    // Types for the component's base type, narrowed to the selected category
    // for Utility/Outdoor. Sorted alphabetically by translation name.
    const availableTypes = useMemo(
        () => getTypesForCategory(buildComponentTypes, component.type, hasCategories ? selectedCategory : null),
        [buildComponentTypes, component.type, hasCategories, selectedCategory]
    );

    // For doors, group the (already sorted) types into exterior/interior/uncategorized
    // sections so inner doors are clearly separated from outer doors in the list.
    const isDoor = component.type === BaseType.Door;
    const doorSections = useMemo(() => {
        if (!isDoor) return [];

        const exterior: typeof availableTypes = [];
        const interior: typeof availableTypes = [];
        const uncategorized: typeof availableTypes = [];

        availableTypes.forEach((entry) => {
            const doorCategory = entry[1].doorProps?.doorCategory;
            if (doorCategory === DoorCategory.exterior_door) {
                exterior.push(entry);
            } else if (doorCategory === DoorCategory.interior_door) {
                interior.push(entry);
            } else {
                uncategorized.push(entry);
            }
        });

        return [
            { label: 'Ytterdörrar', types: exterior },
            { label: 'Innerdörrar', types: interior },
            { label: 'Okategoriserade', types: uncategorized },
        ].filter((section) => section.types.length > 0);
    }, [isDoor, availableTypes]);

    // True when the current type would be removed by a category-only save
    const currentTypeIncompatible = !!(
        component.specificType?.value &&
        selectedCategory &&
        buildComponentTypes[component.specificType.value]?.category !== selectedCategory
    );

    const handleSave = async (selection: SpecificTypeSelection) => {
        setSaving(true);
        setSavingType(selection.typeKey);
        setError(null);
        try {
            await onSave(selection);
            onClose();
        } catch (error) {
            console.error('Error saving specificType:', error);
            setError('Ett fel uppstod vid sparande av typ. Försök igen.');
        } finally {
            setSaving(false);
            setSavingType(null);
        }
    };

    const handleTypeSelect = (typeKey: string) =>
        handleSave({ typeKey, category: hasCategories ? selectedCategory : null });

    const handleSaveCategoryOnly = () =>
        handleSave({ typeKey: null, category: selectedCategory });

    const handleCategorySelect = (category: ComponentCategory) => {
        setSelectedCategory(category);
        setView('type');
    };

    const canCreateNewType =
        newTypeName.trim().length > 0 &&
        newTypeLifetimeYears > 0 &&
        Number.isFinite(newTypeReplacementCost) &&
        newTypeReplacementCost >= 0;

    const resetCreateForm = () => {
        setShowCreateForm(false);
        setNewTypeName('');
        setNewTypeLifetimeYears(NaN);
        setNewTypeReplacementCost(NaN);
        setNewTypeCostUnit(defaultCostUnitForBaseType(component.type));
        setCreatedTypeKey(null);
        setError(null);
    };

    // Picking a library entry fills lifetime, cost and unit; they stay editable
    // and nothing is saved until "Skapa och välj"
    const handleLibrarySelect = (template: LibraryLifetimeTemplate) => {
        setNewTypeLifetimeYears(template.expectedLifetimeYears);
        setNewTypeReplacementCost(template.replacementCost);
        setNewTypeCostUnit(template.costUnit);
        setShowLibraryPicker(false);
    };

    const handleCreateNewType = async () => {
        if (!canCreateNewType) {
            setError('Ange namn, livslängd och utbyteskostnad för den nya typen.');
            return;
        }

        setCreating(true);
        setError(null);
        let newTypeKey = createdTypeKey;
        try {
            if (!newTypeKey) {
                // Create custom type via API layer, stamped with the selected category
                newTypeKey = await componentFieldsApi.createCustomType(
                    component.type,
                    newTypeName.trim(),
                    {
                        expectedLifetimeYears: newTypeLifetimeYears,
                        replacementCost: newTypeReplacementCost,
                        costUnit: newTypeCostUnit
                    },
                    (hasCategories ? selectedCategory : null) || undefined
                );
                setCreatedTypeKey(newTypeKey);
            }

            // Refresh the context to load the new type
            await refresh();

            // Automatically select the newly created type
            await handleTypeSelect(newTypeKey);
        } catch (error) {
            console.error('Error creating new type:', error);
            setError(newTypeKey
                ? 'Typen skapades men kunde inte väljas. Försök igen.'
                : 'Ett fel uppstod vid skapande av ny typ. Försök igen.');
        } finally {
            setCreating(false);
        }
    };

    const renderTypeButton = ([key, def]: typeof availableTypes[number]) => {
        const isCurrentType = component.specificType?.value === key;
        const isSaving = savingType === key;

        return (
            <button
                key={key}
                type="button"
                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                onClick={() => handleTypeSelect(key)}
                disabled={saving || creating}
                style={{
                    cursor: saving || creating ? 'not-allowed' : 'pointer',
                    padding: '12px 16px',
                    backgroundColor: isCurrentType ? BRAND_DARK_BLUE : undefined,
                    borderColor: isCurrentType ? BRAND_DARK_BLUE : undefined,
                    color: isCurrentType ? '#fff' : undefined,
                }}
            >
                <span>{buildComponentTypeDisplayName(def, (materialKey) => materials[materialKey]?.translations?.sv)}</span>
                {isSaving ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : isCurrentType ? (
                    <Check size={18} />
                ) : null}
            </button>
        );
    };

    const renderCategoryButton = (category: ComponentCategory) => {
        const isCurrentCategory = currentCategory === category;

        return (
            <button
                key={category}
                type="button"
                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                onClick={() => handleCategorySelect(category)}
                disabled={saving || creating}
                style={{
                    cursor: saving || creating ? 'not-allowed' : 'pointer',
                    padding: '12px 16px',
                    backgroundColor: isCurrentCategory ? BRAND_DARK_BLUE : undefined,
                    borderColor: isCurrentCategory ? BRAND_DARK_BLUE : undefined,
                    color: isCurrentCategory ? '#fff' : undefined,
                }}
            >
                <span>{translate(category)}</span>
                <ChevronRight size={18} />
            </button>
        );
    };

    const renderCategoryView = () => (
        <>
            <p className="text-muted mb-3">
                Välj kategori för att se tillgängliga typer. Kategori och typ sparas tillsammans.
            </p>
            <div className="list-group mb-3">
                {categories.map(renderCategoryButton)}
            </div>
        </>
    );

    const renderTypeView = () => (
        <>
            {hasCategories && (
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <span className="text-muted">Kategori: </span>
                        <strong>{selectedCategory ? translate(selectedCategory) : '–'}</strong>
                    </div>
                    <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={() => setView('category')}
                        disabled={saving || creating}
                    >
                        Byt kategori
                    </button>
                </div>
            )}
            {availableTypes.length === 0 ? (
                <div className="alert alert-warning">
                    <p className="mb-0">Inga typer tillgängliga för denna komponent</p>
                </div>
            ) : (
                <>
                    <p className="text-muted mb-3">
                        Klicka på en typ för att ange den för denna komponent
                    </p>
                    {isDoor ? (
                        doorSections.map((section) => (
                            <div key={section.label} className="mb-3">
                                <div className="text-muted text-uppercase small fw-bold mb-1">
                                    {section.label}
                                </div>
                                <div className="list-group">
                                    {section.types.map(renderTypeButton)}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="list-group mb-3">
                            {availableTypes.map(renderTypeButton)}
                        </div>
                    )}
                </>
            )}
            <button
                type="button"
                className="btn btn-outline-primary w-100"
                onClick={() => setShowCreateForm(true)}
                disabled={saving || creating}
            >
                <Plus size={16} className="me-2" />
                Skapa ny typ
            </button>
            {hasCategories && selectedCategory && selectedCategory !== currentCategory && (
                <div className="mt-3">
                    <button
                        type="button"
                        className="btn btn-outline-secondary w-100"
                        onClick={handleSaveCategoryOnly}
                        disabled={saving || creating}
                    >
                        {saving && savingType === null ? (
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        ) : null}
                        Spara endast kategori
                    </button>
                    {currentTypeIncompatible && (
                        <p className="text-muted small mt-2 mb-0">
                            Nuvarande typ tas bort eftersom den inte tillhör kategorin{' '}
                            {translate(selectedCategory)}.
                        </p>
                    )}
                </div>
            )}
        </>
    );

    return (
        <>
            <div
                className="modal show d-block"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                onMouseDown={(e) => {
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                <Tag size={20} className="me-2" />
                                {view === 'category' ? 'Välj kategori' : 'Ange typ'} - {building && component.displayName(building)}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                aria-label="Stäng"
                                disabled={saving || creating}
                            />
                        </div>
                        <div className="modal-body">
                            {error && (
                                <div className="alert alert-danger py-2" role="alert">
                                    {error}
                                </div>
                            )}
                            {showCreateForm ? (
                                <div>
                                    <p className="text-muted mb-3">
                                        Skapa en ny anpassad typ för {building && component.displayName(building)}
                                        {hasCategories && selectedCategory && (
                                            <> i kategorin <strong>{translate(selectedCategory)}</strong></>
                                        )}
                                    </p>
                                    <form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            if (canCreateNewType && !creating) handleCreateNewType();
                                        }}
                                    >
                                        <div className="mb-3">
                                            <label htmlFor="newTypeName" className="form-label">
                                                Namn på typ
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="newTypeName"
                                                value={newTypeName}
                                                onChange={(e) => setNewTypeName(e.target.value)}
                                                placeholder="Ange namn på typ"
                                                disabled={creating || !!createdTypeKey}
                                                autoFocus
                                            />
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="fw-medium">Livslängd och utbyteskostnad</span>
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => setShowLibraryPicker(true)}
                                                disabled={creating || !!createdTypeKey}
                                            >
                                                <Library size={14} className="me-1" />
                                                Hämta från livslängdsbiblioteket
                                            </button>
                                        </div>
                                        <div className="row g-2 mb-3">
                                            <div className="col-12 col-sm-4">
                                                <label htmlFor="newTypeLifetime" className="form-label small">
                                                    Livslängd (år)
                                                </label>
                                                <NumberInput
                                                    id="newTypeLifetime"
                                                    className="form-control"
                                                    min="1"
                                                    step="1"
                                                    integer
                                                    emptyValue={NaN}
                                                    value={newTypeLifetimeYears}
                                                    onChange={setNewTypeLifetimeYears}
                                                    placeholder="T.ex. 25"
                                                    disabled={creating || !!createdTypeKey}
                                                />
                                            </div>
                                            <div className="col-12 col-sm-4">
                                                <label htmlFor="newTypeCost" className="form-label small">
                                                    Utbyteskostnad
                                                </label>
                                                <NumberInput
                                                    id="newTypeCost"
                                                    className="form-control"
                                                    min="0"
                                                    step="100"
                                                    emptyValue={NaN}
                                                    value={newTypeReplacementCost}
                                                    onChange={setNewTypeReplacementCost}
                                                    placeholder="T.ex. 2500"
                                                    disabled={creating || !!createdTypeKey}
                                                />
                                            </div>
                                            <div className="col-12 col-sm-4">
                                                <label htmlFor="newTypeCostUnit" className="form-label small">
                                                    Kostnadsenhet
                                                </label>
                                                <select
                                                    id="newTypeCostUnit"
                                                    className="form-select"
                                                    value={newTypeCostUnit}
                                                    onChange={(e) => setNewTypeCostUnit(e.target.value as CostUnit)}
                                                    disabled={creating || !!createdTypeKey}
                                                >
                                                    {COST_UNIT_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-12 form-text mt-1">
                                                Ange 0 om typen inte har någon utbyteskostnad.
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <button
                                                type="submit"
                                                className="btn btn-primary"
                                                disabled={creating || !canCreateNewType}
                                            >
                                                {creating ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Skapar...
                                                    </>
                                                ) : createdTypeKey ? (
                                                    'Välj typen'
                                                ) : (
                                                    'Skapa och välj'
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={resetCreateForm}
                                                disabled={creating}
                                            >
                                                Avbryt
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            ) : view === 'category' ? (
                                renderCategoryView()
                            ) : (
                                renderTypeView()
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                                disabled={saving || creating}
                            >
                                Stäng
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
            <LibraryPickerModal
                config={lifetimeLibrary}
                open={showLibraryPicker}
                onClose={() => setShowLibraryPicker(false)}
                onSelect={handleLibrarySelect}
                currentBaseType={component.type}
            />
        </>
    );
};
